import * as React from 'react'

import moment from 'moment'
import { duration as momentDuration } from 'moment'
import type { Moment } from 'moment'
import { ActivityModel } from 'app/models'
import { ActivityType } from 'app/constants/activity'
import { getActivityType } from 'app/services/activity/activityTypeResolver'
import { formatDuration } from 'app/common/dateHelper'

import FlexView from '@/modules/shared/components/FlexView'
import { Slot } from 'app/constants/slot'

const LABEL_WIDTH = 40
const DAY_HOURS = 24

const padDigits = (number, digits) => {
  return (
    Array(Math.max(digits - String(number).length + 1, 0)).join('0') + number
  )
}

export type GridLabel = {
  label: string
  fraction: number
}

/**
 * Generates grid labels for the timeline chart.
 * Uses moment iteration to handle DST transitions correctly:
 * - Spring forward (e.g. 2:00→3:00): skipped hour is omitted from labels (1, 3)
 * - Fall back (e.g. 3:00→2:00): repeated hour appears twice (1, 2, 2, 3)
 */
export const getLabels = (
  width: number,
  startTime: Moment,
  isMonth: boolean,
): GridLabel[] => {
  if (width == null) return []

  if (isMonth) {
    const labels: GridLabel[] = []
    const totalLabels = 29
    for (let day = 0; day < 14; day++) {
      labels.push({ label: '0', fraction: (day * 2) / (totalLabels - 1) })
      labels.push({ label: '12', fraction: (day * 2 + 1) / (totalLabels - 1) })
    }
    labels.push({ label: '0', fraction: 1 })
    return labels
  }

  // Daily view – DST aware
  let count = width / LABEL_WIDTH
  if (count > DAY_HOURS) count = DAY_HOURS
  const interval = Math.ceil(DAY_HOURS / count)

  const dayStart = moment(startTime).clone()
  const dayEnd = dayStart.clone().add(1, 'days')
  const actualDaySeconds = dayEnd.diff(dayStart, 'seconds')

  if (actualDaySeconds <= 0) return []

  // First whole hour at or after startTime
  let firstHour = dayStart.clone()
  if (dayStart.minutes() !== 0 || dayStart.seconds() !== 0) {
    firstHour = dayStart.clone().startOf('hour').add(1, 'hour')
  }

  // Build all hour marks using moment iteration (handles DST automatically)
  // On spring-forward days moment.add(1,'hour') skips the nonexistent hour,
  // on fall-back days it produces the repeated hour twice.
  const allMarks: GridLabel[] = []
  const current = firstHour.clone()

  while (current.isSameOrBefore(dayEnd)) {
    const secondsFromStart = current.diff(dayStart, 'seconds')
    const fraction = secondsFromStart / actualDaySeconds

    let label: string
    // Show "24" for midnight crossings (distinguish from start-of-day "00")
    if (
      current.hours() === 0 &&
      current.minutes() === 0 &&
      secondsFromStart > 0
    ) {
      label = padDigits(24, 2)
    } else {
      label = padDigits(current.hours(), 2)
    }

    allMarks.push({ label, fraction: Math.min(fraction, 1) })
    current.add(1, 'hour')
  }

  if (allMarks.length === 0) return []

  // Apply interval thinning for narrow charts
  if (interval <= 1) return allMarks

  const result: GridLabel[] = []
  for (let i = 0; i < allMarks.length; i++) {
    if (i === 0 || i === allMarks.length - 1 || i % interval === 0) {
      result.push(allMarks[i])
    }
  }

  return result
}

export const calculatePosition = (
  startDate: Moment,
  time: Moment,
  containerWidth: number,
  days: number,
) => {
  if (startDate == null || time == null || containerWidth == null) return 0

  const convertedStartDate = moment(startDate)
  // Compute actual seconds in the period (handles DST: 23h/25h days)
  const secondsInPeriod = convertedStartDate
    .clone()
    .add(days, 'days')
    .diff(convertedStartDate, 'seconds')

  if (secondsInPeriod <= 0) return 0

  const convertedOriginalTime = moment(time)
  const convertedTime =
    convertedOriginalTime < convertedStartDate
      ? convertedStartDate
      : convertedOriginalTime

  const seconds = convertedTime.diff(convertedStartDate, 'seconds')
  const oneSecondWidth = containerWidth / secondsInPeriod
  const position = seconds * oneSecondWidth

  return position
}

export const calculateSummary = (
  startDate: Moment,
  endDate: Moment,
  activities: ReadonlyArray<ActivityModel>,
  slot: Slot = null,
) => {
  const filteredData = []
  const startTime = startDate
  const endTime = endDate

  if (activities != null) {
    for (let i = 0; i < activities.length; i++) {
      const item = activities[i]

      if (slot != null && item.slot != slot) {
        continue
      }

      if (
        moment(item.endTime) < startTime ||
        moment(item.startTime) > endTime
      ) {
        continue
      }

      // if (moment(item.endTime).toDate().getFullYear() == 9999 ||
      //     moment(item.startTime).toDate().getFullYear() == 1) {
      //     continue;
      // }

      filteredData.push(item)
    }
  }

  const activityDurations = {
    [ActivityType.DRIVING]: 0,
    [ActivityType.WORK]: 0,
    [ActivityType.AVAILABILITY]: 0,
    [ActivityType.REST]: 0,
    [ActivityType.UNKNOWN]: 0,
  }

  if (filteredData.length > 0) {
    filteredData.forEach((activity) => {
      let st = moment(activity.startTime)
      if (st < startDate) {
        st = startDate
      }

      const dayEnd = endDate
      let et = moment(activity.endTime)
      if (moment(activity.endTime) > dayEnd) {
        et = dayEnd
      }

      const duration = momentDuration(moment(et).diff(moment(st)))
      activityDurations[getActivityType(activity.type)] +=
        duration.asMilliseconds()
    })
  }

  return activityDurations
}

export const getFormattedCalculation = (activityDurations) => {
  const activityStringDurations = {
    [ActivityType.DRIVING]: formatDuration(
      momentDuration(activityDurations[ActivityType.DRIVING]),
    ),
    [ActivityType.WORK]: formatDuration(
      momentDuration(activityDurations[ActivityType.WORK]),
    ),
    [ActivityType.AVAILABILITY]: formatDuration(
      momentDuration(activityDurations[ActivityType.AVAILABILITY]),
    ),
    [ActivityType.REST]: formatDuration(
      momentDuration(activityDurations[ActivityType.REST]),
    ),
    [ActivityType.UNKNOWN]: formatDuration(
      momentDuration(activityDurations[ActivityType.UNKNOWN]),
    ),
  }

  return activityStringDurations
}

export const renderSummary = (
  title,
  subtitle,
  driving,
  work,
  availability,
  rest,
  unknown,
  highlight: boolean = false,
  margin: number = 10,
) => {
  const size = 14
  const height = 60

  return (
    <FlexView
      style={{ width: '100%' }}
      height={height}
      vAlignContent="center"
      hAlignContent="center"
    >
      <FlexView
        height={height}
        style={{
          width: '35%',
          color: '#565656',
          background: highlight ? '#FCBABA' : '#e1e1e1',
        }}
        vAlignContent="center"
        hAlignContent="center"
        column
      >
        <FlexView
          style={{ fontSize: 15, fontWeight: 500 }}
          vAlignContent="center"
          hAlignContent="center"
        >
          <span>{title}</span>
        </FlexView>
        <FlexView
          style={{ color: '#565656', fontSize: 13, fontWeight: 500 }}
          vAlignContent="center"
          hAlignContent="center"
        >
          <span>{subtitle}</span>
        </FlexView>
      </FlexView>

      <FlexView
        style={{
          width: '65%',
          color: '#565656',
          background: highlight ? '#EDF8F4' : '#f9f9f9',
        }}
        height={height}
        vAlignContent="center"
        hAlignContent="left"
      >
        <FlexView
          style={{ width: '25%', fontSize: size, marginLeft: 20 }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/driving-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{driving}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/work-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{work}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/availability-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{availability}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/rest-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{rest}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/unknown.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{unknown}</span>
        </FlexView>
      </FlexView>
    </FlexView>
  )
}

export const renderSimpleSummary = (
  dateFrom,
  dateTo,
  driving,
  work,
  availability,
  rest,
  unknown,
  highlight: boolean = false,
  margin: number = 10,
) => {
  const size = 14
  const height = 45

  return (
    <FlexView
      style={{ width: '100%' }}
      height={height}
      vAlignContent="center"
      hAlignContent="center"
    >
      <FlexView
        height={height}
        style={{
          width: '25%',
          color: '#565656',
          background: highlight ? '#FCBABA' : '#e1e1e1',
        }}
        vAlignContent="center"
        hAlignContent="center"
        column
      >
        <FlexView
          style={{ color: '#565656', fontSize: 13, fontWeight: 500 }}
          vAlignContent="center"
          hAlignContent="center"
        >
          <span>{dateFrom}</span>
        </FlexView>
        <FlexView
          style={{ color: '#565656', fontSize: 13, fontWeight: 500 }}
          vAlignContent="center"
          hAlignContent="center"
        >
          <span>{dateTo}</span>
        </FlexView>
      </FlexView>

      <FlexView
        style={{
          width: '75%',
          color: '#565656',
          background: highlight ? '#EDF8F4' : '#f9f9f9',
        }}
        height={height}
        vAlignContent="center"
        hAlignContent="left"
      >
        <FlexView
          style={{ width: '25%', fontSize: size, marginLeft: 20 }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/driving-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{driving}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/work-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{work}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/availability-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{availability}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/rest-black.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{rest}</span>
        </FlexView>
        <FlexView
          style={{ width: '25%', fontSize: size }}
          vAlignContent="center"
          hAlignContent="left"
        >
          <img
            src="/assets/icons_nucleo/icons/activity/unknown.svg"
            width={size}
            height={size}
          />
          <span style={{ marginLeft: margin }}>{unknown}</span>
        </FlexView>
      </FlexView>
    </FlexView>
  )
}
